import { useEffect, useState } from 'react'
import { applyTheme, readChoice, watchSystemTheme, type ThemeChoice } from '../lib/theme'
import { useT } from '../lib/i18n'
import styles from './ThemePicker.module.css'

/**
 * THE THEME CONTROL — three states, and the middle one is the default.
 *
 * ⚠ `system` IS FIRST AND IS WHAT AN UNTOUCHED HEIMAR USES. Somebody who has told their phone they
 * want light at noon has already answered this question once, and asking again is the product
 * making them hold an opinion it could have inferred.
 *
 * ⚠ IT LIVES ON THE SHELF, NEVER INSIDE A SITTING. Same rule as the wallet: the shelf is where the
 * reader configures, and the surface is where they read. A theme control inside a thread is chrome
 * arriving in the middle of a conversation.
 *
 * The `<select>` follows `LocalePicker`'s reasoning exactly — one element, the OS-native sheet on
 * mobile, keyboard and screen-reader support free, and it costs the same at three options as at
 * thirty.
 */
export default function ThemePicker({ className }: { className?: string }) {
  const t = useT()
  const [choice, setChoice] = useState<ThemeChoice>(readChoice)

  // Follow the system while the reader has expressed no preference — a phone that switches at dusk
  // should take the page with it.
  useEffect(watchSystemTheme, [])

  return (
    <label className={`${styles.wrap} ${className ?? ''}`}>
      <select
        className={styles.select}
        aria-label={t('shelf.theme')}
        value={choice}
        onChange={(e) => {
          const next = e.target.value as ThemeChoice
          setChoice(next)
          applyTheme(next)
        }}
      >
        <option value="system">{t('shelf.themeSystem')}</option>
        <option value="dark">{t('shelf.themeDark')}</option>
        <option value="light">{t('shelf.themeLight')}</option>
      </select>
    </label>
  )
}
